import type { BotProfile, WebsiteChannel } from '../../api/types';

/**
 * The Chatbot screen's setup rules, kept out of the components so they can be
 * pinned — the analytics install screen's `installForm.ts`, for the chat.
 *
 * Every check here mirrors what the SERVICE does with a value:
 *
 * - Allowed sites are compared to the page's origin by EXACT string
 *   (`channels/website/origin.js`), so `example.com` typed for a site served
 *   on `https://www.example.com` is a list that refuses every page of it —
 *   and a refused page gets no chat at all, which looks exactly like a snippet
 *   that was never pasted.
 * - The adapter's `parseOrigins` refuses anything but a bare origin, so the
 *   screen reduces what was typed to one before sending rather than letting
 *   the save fail on a trailing slash.
 * - "Live" is the widget's own boot stamping the channel (`lastSeenAt`, at most
 *   every ten minutes, never from the console's preview) — read, not asserted.
 */

export type ChatSection = 'install' | 'assistant' | 'knowledge' | 'widget' | 'channels' | 'playground' | 'activity';

export const CHAT_SECTIONS: readonly ChatSection[] = [
  'install',
  'assistant',
  'knowledge',
  'widget',
  'channels',
  'playground',
  'activity',
];

export const isChatSection = (value: unknown): value is ChatSection =>
  CHAT_SECTIONS.includes(value as ChatSection);

/** The colour a new website chat is created with, and what "not branded yet" means. */
export const DEFAULT_WIDGET_COLOR = '#0E7C86';

/* ---- is it live ---------------------------------------------------------- */

export type ChatLive = 'live' | 'quiet' | 'waiting';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the chat is on a site. `quiet` is a day without a page load: on a
 * small site that can be a quiet day, and on any site it is also what a
 * redesign that dropped the snippet looks like, so it is a prompt to look
 * rather than an alarm.
 */
export const chatLive = (channel: Pick<WebsiteChannel, 'lastSeenAt'>, now = Date.now()): ChatLive => {
  if (!channel.lastSeenAt) return 'waiting';
  const at = Date.parse(channel.lastSeenAt);
  if (Number.isNaN(at)) return 'waiting';
  return now - at > DAY_MS ? 'quiet' : 'live';
};

/** `https://www.example.com` → `www.example.com`: how a person names their site. */
export const siteName = (origin: string | null | undefined): string => {
  if (!origin) return '';
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
};

/* ---- the snippet --------------------------------------------------------- */

export { BUTTON_SNIPPET, IDENTITY_SNIPPET, chatSnippet } from '../../lib/chatSnippet';

/* ---- allowed sites ------------------------------------------------------- */

export interface SiteProblem {
  line: string;
  message: string;
}

export interface SiteList {
  /** What will be saved: bare origins, in the order typed, each once. */
  origins: string[];
  problems: SiteProblem[];
  /**
   * Sites that will refuse a page the operator very likely meant to allow:
   * `example.com` allowed and `www.example.com` not, or the other way round.
   */
  missingTwins: string[];
}

/**
 * One line → the origin the service will store, or null. A bare host is read
 * as https, because that is what someone typing `example.com` means; a path,
 * a query or a trailing slash is dropped, because the browser never reports
 * one and the service refuses them.
 */
export const siteOrigin = (raw: string): string | null => {
  const line = raw.trim();
  if (!line) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(line) ? line : `https://${line}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!url.hostname || /\s/.test(line) || line.includes('*')) return null;
    return url.origin;
  } catch {
    return null;
  }
};

const twinOf = (origin: string): string | null => {
  try {
    const url = new URL(origin);
    // An address or localhost has no www twin, and neither does a host that is
    // already a deeper subdomain than one label past the domain.
    if (/^[\d.]+$/.test(url.hostname) || !url.hostname.includes('.')) return null;
    const host = url.hostname.startsWith('www.') ? url.hostname.slice(4) : `www.${url.hostname}`;
    if (!url.hostname.startsWith('www.') && url.hostname.split('.').length > 2) return null;
    return `${url.protocol}//${host}${url.port ? `:${url.port}` : ''}`;
  } catch {
    return null;
  }
};

export const siteList = (text: string): SiteList => {
  const origins: string[] = [];
  const problems: SiteProblem[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const origin = siteOrigin(line);
    if (!origin) {
      problems.push({
        line,
        message: line.includes('*')
          ? 'is a pattern — list each site on its own line, like https://www.example.com.'
          : 'is not a web address — use the address your site opens on, like https://www.example.com.',
      });
      continue;
    }
    if (!origins.includes(origin)) origins.push(origin);
  }
  const missingTwins = origins
    .map(twinOf)
    .filter((twin): twin is string => twin !== null && !origins.includes(twin));
  return { origins, problems, missingTwins };
};

export const sameOrigins = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/* ---- the checklist ------------------------------------------------------- */

export interface ChatCheck {
  key: 'enabled' | 'answers' | 'context' | 'sites' | 'brand';
  label: string;
  detail: string;
  done: boolean;
  section: ChatSection;
}

/** Shorter than this is a placeholder, not a description of a business. */
const CONTEXT_MIN = 40;

/**
 * What is left before the chat is worth putting in front of a customer, read
 * off the profile and the channel themselves — never a box somebody ticks.
 * Everything here is something the operator can see the effect of; "tried it
 * in the Playground" is not, which is why it is not on the list.
 */
export const chatChecklist = (
  profile: Pick<BotProfile, 'enabled' | 'inboxIds' | 'systemContext'>,
  inbox: { id: number; channel: Pick<WebsiteChannel, 'allowedOrigins' | 'logoS3Key' | 'widgetColor'> },
): ChatCheck[] => {
  const color = (inbox.channel.widgetColor || DEFAULT_WIDGET_COLOR).toLowerCase();
  return [
    {
      key: 'answers',
      label: 'Let the assistant answer this chat',
      detail: 'Until it does, every visitor waits for your team.',
      done: profile.inboxIds.includes(inbox.id),
      section: 'install',
    },
    {
      key: 'enabled',
      label: 'Switch the assistant on',
      detail: 'Try it in the Playground, then use the switch at the top of this screen.',
      done: profile.enabled,
      // The switch itself is in the header, on every section; what is worth
      // opening first is what it will say.
      section: 'playground',
    },
    {
      key: 'context',
      label: 'Tell it about your business',
      detail: 'The business context decides whether its answers are any good.',
      done: (profile.systemContext ?? '').trim().length >= CONTEXT_MIN,
      section: 'assistant',
    },
    {
      key: 'sites',
      label: 'Keep it to your own site',
      detail: 'Name your site, so the chat cannot be put on anybody else’s.',
      done: (inbox.channel.allowedOrigins ?? []).length > 0,
      section: 'install',
    },
    {
      key: 'brand',
      label: 'Brand the widget',
      detail: 'Your logo and colour, so visitors know who is answering.',
      done: Boolean(inbox.channel.logoS3Key) || color !== DEFAULT_WIDGET_COLOR.toLowerCase(),
      section: 'widget',
    },
  ];
};

/* ---- links into the screen ---------------------------------------------- */

/** Where to open the screen, from outside it. */
export interface ChatLink {
  section: ChatSection;
  /** The website chat to show, when the link names one. */
  inboxId: number | null;
}

/**
 * `?section=install&inbox=12` — the dispatch dashboard's Website chat card,
 * and the framed page under My website. Null for anything else, so a
 * malformed link lands on the screen as if there were none: a link from
 * outside is a suggestion, and must not become an error page.
 */
export const chatLinkOf = (search: string): ChatLink | null => {
  const params = new URLSearchParams(search);
  const section = params.get('section');
  const rawInbox = params.get('inbox');
  const inboxId = rawInbox === null ? null : Number(rawInbox);
  if (!isChatSection(section) && rawInbox === null) return null;
  return {
    section: isChatSection(section) ? section : 'install',
    inboxId: inboxId !== null && Number.isSafeInteger(inboxId) && inboxId > 0 ? inboxId : null,
  };
};
